import MuiChip, { ChipProps as MuiChipProps } from '@mui/material/Chip';
import { ReactNode } from 'react';

/**
 * Semantic color type for the Chip, aligned with previous Badge usage.
 * Maps to MUI Chip color: danger → error, light → default, yellow → warning.
 */
export type ChipColorType =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'danger'
  | 'light'
  | 'warning'
  | 'yellow';

export type ChipProps = Omit<MuiChipProps, 'color' | 'size' | 'label' | 'children'> & {
  /** Semantic color variant. */
  type?: ChipColorType;
  /** Size of the chip. */
  size?: 'sm' | 'md' | 'lg';
  /** Content rendered as the chip label. */
  children: ReactNode;
};

const typeToMuiColor: Record<ChipColorType, MuiChipProps['color']> = {
  primary: 'primary',
  secondary: 'secondary',
  success: 'success',
  danger: 'error',
  light: 'default',
  warning: 'warning',
  yellow: 'warning',
};

/**
 * Chip component wrapping MUI Chip. Replaces the previous Badge component
 * with a pill-shaped, filled chip. Use for status labels, tags, and counts.
 */
export function Chip({ type = 'primary', size = 'md', children, sx, ...rest }: ChipProps) {
  const muiColor = typeToMuiColor[type];
  const muiSize = size === 'sm' ? 'small' : 'medium';

  return (
    <MuiChip
      label={children}
      color={muiColor}
      size={muiSize}
      variant="filled"
      sx={[{ borderRadius: '9999px' }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])]}
      {...rest}
    />
  );
}
